import PropTypes from "prop-types";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useIntl } from "react-intl";
import * as UserUploadConstants from "../../../../constants/UserUploadConstants";
import {
  rowResultLabelId,
  statusLabelId,
  statusSeverity,
} from "../../../../helpers/UserUploadHelper";

const ROWS_PER_PAGE = 10;

// Summary of one upload: status, counts, the rows that need attention
// (GET api/users/uploads/{id}: Failed first, at most 1,000) and the results
// file download. Rows are null while they have not been fetched yet.
const UserUploadResult = ({ upload, onDownloadResults }) => {
  const intl = useIntl();
  const message = (id) => intl.formatMessage({ id });
  const rows = upload.rows || [];

  const counts = [
    ["user_upload_count_total", upload.totalRows],
    ["user_upload_count_added", upload.addedCount],
    ["user_upload_count_updated", upload.updatedCount],
    ["user_upload_count_skipped", upload.skippedCount],
    ["user_upload_count_failed", upload.failedCount],
  ];

  const renderRows = () => {
    if (upload.filesExpired) {
      return (
        <Message
          severity="info"
          text={message("user_upload_files_expired_message")}
        />
      );
    }
    if (upload.rows == null) {
      return null;
    }
    if (rows.length === 0) {
      return upload.status === UserUploadConstants.USER_UPLOAD_STATUS_COMPLETED ? (
        <p className="m-0">{message("user_upload_rows_empty")}</p>
      ) : null;
    }
    return (
      <div className="flex flex-column gap-2">
        <strong>{message("user_upload_rows_title")}</strong>
        {(upload.rowsTruncated && (
          <Message
            severity="info"
            text={message("user_upload_rows_truncated")}
          />
        )) ||
          null}
        <DataTable
          value={rows}
          dataKey="rowNumber"
          size="small"
          stripedRows
          paginator={rows.length > ROWS_PER_PAGE}
          rows={ROWS_PER_PAGE}
        >
          <Column field="rowNumber" header={message("user_upload_column_row")} />
          <Column
            field="username"
            header={message("user_upload_column_username")}
          />
          <Column field="email" header={message("user_upload_column_email")} />
          <Column
            header={message("user_upload_column_result")}
            body={(row) => message(rowResultLabelId(row.result))}
          />
          <Column field="reason" header={message("user_upload_column_reason")} />
        </DataTable>
      </div>
    );
  };

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-wrap align-items-center gap-2">
        <strong>{upload.fileName}</strong>
        <Tag
          value={message(statusLabelId(upload.status))}
          severity={statusSeverity(upload.status)}
        />
      </div>
      {(upload.failureReason && (
        <Message severity="error" text={upload.failureReason} />
      )) ||
        null}
      <div className="flex flex-wrap gap-4">
        {counts.map(([id, count]) => (
          <span key={id}>{intl.formatMessage({ id }, { count: count ?? 0 })}</span>
        ))}
      </div>
      {renderRows()}
      {(upload.hasResultsFile && !upload.filesExpired && (
        <div>
          <Button
            type="button"
            outlined
            size="small"
            icon="pi pi-download"
            label={message("user_upload_download_results")}
            onClick={() => onDownloadResults(upload)}
          />
        </div>
      )) ||
        null}
    </div>
  );
};

UserUploadResult.propTypes = {
  upload: PropTypes.object.isRequired,
  onDownloadResults: PropTypes.func.isRequired,
};

export default UserUploadResult;
